import { useMemo } from "react";

// ==================== 类型定义 ====================
/**
 * 评级数据的基础节点类型
 */
export interface RatingNode {
  id: number;
  name: string;
  nodeType: "STAGE" | "DIMENSION" | "ITEM";
  parentId: number;
  excellentNum: number;
  middleNum: number;
  poorNum: number;
  naNum: number;
  childList?: RatingNode[];
  nickName?: string;
  purchaseUserId?: number;
  levelList?: Array<{
    levelName: string;
    checked: boolean;
    description: string;
    remark: string;
  }>;
}

/**
 * 展平后的评级数据记录
 */
export interface FlatRatingRecord {
  id: string;
  stageName: string;
  stageId: number;
  dimensionName: string;
  dimensionId: number;
  itemName: string;
  itemId: number;
  levelName: string;
  description: string;
  remark: string;
  excellentNum: number;
  middleNum: number;
  poorNum: number;
  naNum: number;
  depth: number;
}

/**
 * 评级统计结果
 */
export interface RatingSummary {
  excellentCount: number;
  middleCount: number;
  poorCount: number;
  naCount: number;
  total: number;
}

/**
 * useRatingData Hook 的返回类型
 */
export interface UseRatingDataReturn {
  // 原始树形数据
  excellentTreeData: RatingNode[];
  middleTreeData: RatingNode[];
  poorTreeData: RatingNode[];
  naTreeData: RatingNode[];

  // 展平后的数据
  excellentFlatData: FlatRatingRecord[];
  middleFlatData: FlatRatingRecord[];
  poorFlatData: FlatRatingRecord[];
  naFlatData: FlatRatingRecord[];

  // 统计数据
  excellentSummary: RatingSummary;
  middleSummary: RatingSummary;
  poorSummary: RatingSummary;
  naSummary: RatingSummary;

  // 辅助方法
  hasAnyRatingData: boolean;
}

// ==================== 工具函数 ====================
/**
 * 解析后端返回的 JSON 字符串为树形结构
 */
const parseRatingDataJson = (
  jsonString: string | null | undefined,
): RatingNode[] => {
  if (!jsonString) {
    return [];
  }

  try {
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("评级数据 JSON 解析失败:", error);
    return [];
  }
};

/**
 * 展平树形结构，生成用于表格展示的数组
 * 递归遍历树形数据，为每个 ITEM 节点生成一条展平记录
 */
const flattenRatingData = (nodes: RatingNode[]): FlatRatingRecord[] => {
  const records: FlatRatingRecord[] = [];
  let recordId = 0;

  const traverse = (
    node: RatingNode,
    stageName = "",
    stageId = 0,
    dimensionName = "",
    dimensionId = 0,
    depth = 0,
  ) => {
    if (node.nodeType === "STAGE") {
      stageName = node.name;
      stageId = node.id;
      if (node.childList && Array.isArray(node.childList)) {
        node.childList.forEach((child) =>
          traverse(
            child,
            stageName,
            stageId,
            dimensionName,
            dimensionId,
            depth + 1,
          ),
        );
      }
    } else if (node.nodeType === "DIMENSION") {
      dimensionName = node.name;
      dimensionId = node.id;
      if (node.childList && Array.isArray(node.childList)) {
        node.childList.forEach((child) =>
          traverse(
            child,
            stageName,
            stageId,
            dimensionName,
            dimensionId,
            depth + 1,
          ),
        );
      }
    } else if (node.nodeType === "ITEM") {
      const levelName = node.levelList?.[0]?.levelName || "未知";
      const description = node.levelList?.[0]?.description || "";
      const remark = node.levelList?.[0]?.remark || "";

      records.push({
        id: `${levelName}_${recordId++}`,
        stageName,
        stageId,
        dimensionName,
        dimensionId,
        itemName: node.name,
        itemId: node.id,
        levelName,
        description,
        remark,
        excellentNum: node.excellentNum || 0,
        middleNum: node.middleNum || 0,
        poorNum: node.poorNum || 0,
        naNum: node.naNum || 0,
        depth,
      });
    }
  };

  nodes.forEach((node) => traverse(node));

  return records;
};

/**
 * 计算评级数据的统计信息
 */
const calculateRatingSummary = (
  flatData: FlatRatingRecord[],
): RatingSummary => {
  return {
    excellentCount: flatData.reduce((sum, r) => sum + r.excellentNum, 0),
    middleCount: flatData.reduce((sum, r) => sum + r.middleNum, 0),
    poorCount: flatData.reduce((sum, r) => sum + r.poorNum, 0),
    naCount: flatData.reduce((sum, r) => sum + r.naNum, 0),
    total: flatData.length,
  };
};

// ==================== 自定义Hook ====================
/**
 * useRatingData Hook
 * 处理评级数据的解析、展平和统计
 *
 * @param excellentSummary - 优评 JSON 字符串
 * @param middleSummary - 中评 JSON 字符串
 * @param poorSummary - 差评 JSON 字符串
 * @param naSummary - NA JSON 字符串
 * @returns 包含所有处理结果的对象
 *
 * @example
 * const {
 *   excellentTreeData,
 *   excellentFlatData,
 *   excellentSummary,
 *   hasAnyRatingData,
 * } = useRatingData(formData.excellentSummary, formData.middleSummary, ...);
 */
export const useRatingData = (
  excellentSummary?: string | null,
  middleSummary?: string | null,
  poorSummary?: string | null,
  naSummary?: string | null,
): UseRatingDataReturn => {
  // 解析树形数据
  const excellentTreeData = useMemo(
    () => parseRatingDataJson(excellentSummary),
    [excellentSummary],
  );

  const middleTreeData = useMemo(
    () => parseRatingDataJson(middleSummary),
    [middleSummary],
  );

  const poorTreeData = useMemo(
    () => parseRatingDataJson(poorSummary),
    [poorSummary],
  );

  const naTreeData = useMemo(() => parseRatingDataJson(naSummary), [naSummary]);

  // 展平数据
  const excellentFlatData = useMemo(
    () => flattenRatingData(excellentTreeData),
    [excellentTreeData],
  );

  const middleFlatData = useMemo(
    () => flattenRatingData(middleTreeData),
    [middleTreeData],
  );

  const poorFlatData = useMemo(
    () => flattenRatingData(poorTreeData),
    [poorTreeData],
  );

  const naFlatData = useMemo(() => flattenRatingData(naTreeData), [naTreeData]);

  // 计算统计数据
  const excellentSummaryData = useMemo(
    () => calculateRatingSummary(excellentFlatData),
    [excellentFlatData],
  );

  const middleSummaryData = useMemo(
    () => calculateRatingSummary(middleFlatData),
    [middleFlatData],
  );

  const poorSummaryData = useMemo(
    () => calculateRatingSummary(poorFlatData),
    [poorFlatData],
  );

  const naSummaryData = useMemo(
    () => calculateRatingSummary(naFlatData),
    [naFlatData],
  );

  // 检查是否有任何数据
  const hasAnyRatingData = useMemo(
    () => !!(excellentSummary || middleSummary || poorSummary || naSummary),
    [excellentSummary, middleSummary, poorSummary, naSummary],
  );

  return {
    excellentTreeData,
    middleTreeData,
    poorTreeData,
    naTreeData,
    excellentFlatData,
    middleFlatData,
    poorFlatData,
    naFlatData,
    excellentSummary: excellentSummaryData,
    middleSummary: middleSummaryData,
    poorSummary: poorSummaryData,
    naSummary: naSummaryData,
    hasAnyRatingData,
  };
};
